import React from 'react';
import { Link, useParams } from '@tanstack/react-router';
import Breadcrumbs from '../../components/Breadcrumbs';
import BlogPreview from '../../components/BlogPreview';
import { useSEO, SITE_URL } from '../../hooks/useSEO';
import { getPost, getRelatedPosts } from '../../blog';

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });

function buildStructuredData(post) {
  const url = `${SITE_URL}/blog/${post.slug}/`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.description,
        image: `${SITE_URL}${post.image}`,
        url,
        mainEntityOfPage: url,
        datePublished: post.datePublished,
        dateModified: post.dateModified || post.datePublished,
        inLanguage: 'en',
        keywords: post.tags.join(', '),
        author: { '@type': 'Organization', name: 'Compress GLB', url: `${SITE_URL}/` },
        publisher: { '@type': 'Organization', name: 'Compress GLB', url: `${SITE_URL}/` },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog/` },
          { '@type': 'ListItem', position: 3, name: post.title, item: url },
        ],
      },
    ],
  };
}

export default function BlogPostPage() {
  const { slug } = useParams({ from: '/blog/$slug' });
  const post = getPost(slug);

  useSEO({
    title: post ? post.seoTitle || post.title : 'Post not found – Compress GLB Blog',
    description: post?.description,
    canonical: `/blog/${slug}`,
    robots: post ? 'index, follow' : 'noindex, follow',
    ogTitle: post?.title,
    ogDescription: post?.description,
    ogImage: post?.image,
    ogType: 'article',
    structuredData: post && buildStructuredData(post),
  });

  if (!post) {
    return (
      <div className="text-center py-16">
        <h1 className="text-3xl font-bold text-foreground mb-4">Post not found</h1>
        <Link to="/blog" className="text-accent hover:text-accent/80">Back to the blog</Link>
      </div>
    );
  }

  const { Content } = post;
  const related = getRelatedPosts(post);
  const updated = post.dateModified && post.dateModified !== post.datePublished;

  return (
    <article className="max-w-3xl mx-auto">
      <Breadcrumbs crumbs={[{ label: 'Home', to: '/' }, { label: 'Blog', to: '/blog' }, { label: post.title }]} />

      <header className="mb-8">
        <p className="text-sm font-semibold text-accent uppercase tracking-wide">{post.category}</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">{post.title}</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          {updated ? (
            <>Updated <time dateTime={post.dateModified}>{formatDate(post.dateModified)}</time></>
          ) : (
            <time dateTime={post.datePublished}>{formatDate(post.datePublished)}</time>
          )}
          {' · '}{post.readingMinutes} min read
        </p>
      </header>

      <img
        src={post.image}
        alt={post.imageTitle || post.title}
        width="1200"
        height="630"
        className="w-full h-auto rounded-xl border border-border/60 mb-10"
      />

      <div className="prose lg:prose-lg max-w-none prose-a:text-accent prose-code:before:content-none prose-code:after:content-none prose-pre:bg-transparent prose-pre:p-0 prose-img:rounded-xl">
        <Content />
      </div>

      <aside className="mt-12 p-6 sm:p-8 bg-background-elevated rounded-xl border border-border/60 text-center">
        <h2 className="text-2xl font-bold text-foreground">Make your GLB files up to 90% smaller</h2>
        <p className="mt-3 text-muted-foreground/90">
          Draco geometry compression, texture resizing and WebP/AVIF conversion in one pass. Files up to 500 MB, batch uploads and a REST API.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center justify-center px-5 py-3 text-base font-medium rounded-lg text-primary-foreground bg-primary hover:bg-primary-hover transition-all duration-200 shadow-sm"
        >
          Compress your GLB
        </Link>
      </aside>

      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="text-2xl font-bold text-foreground mb-6">Related articles</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {related.map((other) => (
              <BlogPreview key={other.slug} post={other} compact />
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
